import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Badge,
    Box,
    Button,
    CardActions,
    Chip,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Typography,
    useMediaQuery
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useContactInquirySummary from 'hooks/useContactInquirySummary';

// assets
import { IconBell, IconMail } from '@tabler/icons';

const timeAgo = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const minutes = Math.round((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return 'เมื่อสักครู่';
    if (minutes < 60) return `${minutes} นาทีที่แล้ว`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days} วันที่แล้ว`;
    return date.toLocaleDateString('th-TH', { dateStyle: 'medium' });
};

// ==============================|| NOTIFICATION — NEW CONTACT INQUIRIES ||============================== //

const NotificationSection = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const { enabled, counts, latestNew } = useContactInquirySummary();
    const newCount = counts.new || 0;

    const [open, setOpen] = useState(false);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const openInquiry = (inquiry) => {
        setOpen(false);
        navigate('/contact-inquiries', inquiry ? { state: { inquiry } } : undefined);
    };

    // School-side roles don't receive platform notifications.
    if (!enabled) return null;

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 3,
                    [theme.breakpoints.down('md')]: {
                        mr: 2
                    }
                }}
            >
                <Badge badgeContent={newCount} color="error" max={99} overlap="rectangular">
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                            color: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                            '&[aria-controls="menu-list-grow"],&:hover': {
                                background: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                                color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.secondary.light
                            }
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        aria-label={newCount ? `มีข้อความติดต่อใหม่ ${newCount} เรื่อง` : 'การแจ้งเตือน'}
                        onClick={handleToggle}
                        color="inherit"
                    >
                        <IconBell stroke={1.5} size="20px" />
                    </Avatar>
                </Badge>
            </Box>

            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? 5 : 0, 20]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ width: { xs: 'calc(100vw - 32px)', sm: 360 }, maxWidth: 360 }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2 }}>
                                                <Typography variant="subtitle1">ข้อความติดต่อใหม่</Typography>
                                                {newCount > 0 && (
                                                    <Chip
                                                        size="small"
                                                        label={newCount > 99 ? '99+' : newCount}
                                                        color="error"
                                                        sx={{ height: 20 }}
                                                    />
                                                )}
                                            </Stack>
                                            <Divider />
                                            {latestNew.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                                                    ไม่มีเรื่องใหม่ที่รอติดต่อกลับ
                                                </Typography>
                                            ) : (
                                                <PerfectScrollbar style={{ maxHeight: 'calc(100vh - 220px)', overflowX: 'hidden' }}>
                                                    <List disablePadding>
                                                        {latestNew.map((item) => (
                                                            <ListItemButton
                                                                key={item.id}
                                                                onClick={() => openInquiry(item)}
                                                                sx={{ alignItems: 'flex-start', gap: 1.5, py: 1.5 }}
                                                            >
                                                                <Avatar
                                                                    sx={{
                                                                        width: 34,
                                                                        height: 34,
                                                                        bgcolor: theme.palette.error.light,
                                                                        color: theme.palette.error.dark
                                                                    }}
                                                                >
                                                                    <IconMail stroke={1.5} size="18px" />
                                                                </Avatar>
                                                                <ListItemText
                                                                    primary={
                                                                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                                            <Typography variant="subtitle2" noWrap>
                                                                                {item.fullName}
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                                sx={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                {timeAgo(item.createdAt)}
                                                                            </Typography>
                                                                        </Stack>
                                                                    }
                                                                    secondary={
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                            sx={{
                                                                                display: '-webkit-box',
                                                                                WebkitLineClamp: 2,
                                                                                WebkitBoxOrient: 'vertical',
                                                                                overflow: 'hidden'
                                                                            }}
                                                                        >
                                                                            {item.organization ? `${item.organization} · ` : ''}
                                                                            {item.message}
                                                                        </Typography>
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        ))}
                                                    </List>
                                                </PerfectScrollbar>
                                            )}
                                            <Divider />
                                            <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                                                <Button size="small" disableElevation onClick={() => openInquiry(null)}>
                                                    ดูข้อความติดต่อทั้งหมด
                                                </Button>
                                            </CardActions>
                                        </Box>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
